import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AssignShopRequest, CreateMarketAreaRequest, GMSearchResult, TypeAheadConfig, User } from '@org/interfaces';
import { AuthService, MarketAreaService, SysNotifyService, UserService, DASHBOARD_HTTP_ONLY_REFRESH_MS } from '@org/shared-services';
import { BehaviorSubject, Observable, Subscription, timer } from 'rxjs';
import { switchMap, tap, finalize, catchError } from 'rxjs/operators';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { 
  AssignedShopsResponse, 
  MarketAreaDailyReport, 
  ShopAssignmentHistory 
} from '@org/interfaces';
import { TypeAheadComponent } from '@org/components';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';

@Component({
  selector: 'lib-am-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    TypeAheadComponent
  ],
  templateUrl: './am-dashboard.component.html',
  styleUrl: './am-dashboard.component.scss'
})
export class AmDashboardComponent implements OnInit, OnDestroy {
  // User and Market Area Data
  user: User | null = null;
  assignedShops: AssignedShopsResponse['data'] = [];
  dailyReport: MarketAreaDailyReport | null = null;
  shopHistory: ShopAssignmentHistory[] = [];

  // Loading States
  loading = {
    shops: false,
    dailyReport: false,
    history: false
  };
  error = {
    shops: '',
    dailyReport: '',
    history: ''
  };

  // Refresh Configuration
  private refreshInterval = DASHBOARD_HTTP_ONLY_REFRESH_MS;
  private subscriptions = new Subscription();
  private dataUpdateSubject = new BehaviorSubject<void>(undefined);
  lastUpdateTime: Date = new Date();

  // Date Selection
  selectedDate: string = new Date().toISOString().split('T')[0]; // YYYY-MM-DD

  // Setup wizard properties
  isSetupComplete: boolean = true;
  setupSteps: {
    hasMarketArea?: boolean;
    hasAssignedShops?: boolean;
    hasValidRole?: boolean;
    hasValidTenant?: boolean;
  } = {};
  nextSteps: string[] = [];
  showSetupWizard: boolean = false;

  // Onboarding wizard modal state and form
  showCreateMarketAreaModal: boolean = false;
  marketAreaForm!: FormGroup;
  marketAreaFormError: string = '';
  marketAreaFormLoading: boolean = false;

  // GM search state
  gmSearchResults: any[] = [];
  selectedGM: any = null;
  gmSearchLoading: boolean = false;

  // Add selectedGMs array to hold selected GMs
  selectedGMs: Array<{ name: string; userId: string; tenantId: string; tenantName: string; tenantAddress?: string }> = [];

  gmTypeAheadConfig: TypeAheadConfig<GMSearchResult> = {
    searchFn: (term: string) => this.userService.searchGMs(term),
    displayFn: (gm: any) => `${gm.name} (${gm.email})`,
    valueFn: (gm: any) => gm,
    multiSelect: true,
    minLength: 2,
    debounceTime: 300,
    placeholder: 'Search GM by name',
    notFoundText: 'No GMs found',
    clearOnSelect: true,
  };

  assignedShopsForm: AssignShopRequest[] = [];

  timeUntilNextPoll: string = '';

  constructor(
    private authService: AuthService,
    private marketAreaService: MarketAreaService,
    private userService: UserService,
    private cdr: ChangeDetectorRef,
    private fb: FormBuilder,
    private sysNotifyService: SysNotifyService,
  ) {}

  ngOnInit(): void {
    this.checkSetupStatus();
    this.setupDataRefresh();
    this.loadInitialData();
    this.loadUserData();
    // Initialize reactive form
    this.marketAreaForm = this.fb.group({
      name: ['', Validators.required],
      region: [''],
      corporateTenant: [{ value: '', disabled: true }],
      marketAreaManager: [{ value: '', disabled: true }],
      gmSearch: ['', Validators.required]
    });
    // Update timeUntilNextPoll every second to avoid ExpressionChangedAfterItHasBeenCheckedError
    setInterval(() => {
      this.timeUntilNextPoll = this.getTimeUntilNextPoll();
      this.cdr.detectChanges();
    }, 1000);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private setupDataRefresh(): void {
    const refresh$ = timer(0, this.refreshInterval).pipe(
      switchMap(() => this.dataUpdateSubject)
    );

    this.subscriptions.add(
      refresh$.subscribe(() => {
        this.loadInitialData();
      })
    );
  }

  private loadUserData(): void {
    this.subscriptions.add(
      this.userService.getUser().subscribe({
        next: (user) => {
          this.user = user;
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error loading user data:', error);
          this.cdr.markForCheck();
        }
      })
    );
  }

  private loadInitialData(): void {
    this.loadAssignedShops();
    this.loadDailyReport();
  }

  private loadAssignedShops(): void {
    this.loading.shops = true;
    this.error.shops = '';

    this.subscriptions.add(
      this.marketAreaService.getAssignedShops().pipe(
        finalize(() => {
          this.loading.shops = false;
          this.cdr.markForCheck();
        })
      ).subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.assignedShops = response.data;
            // // Load history for each shop
            this.assignedShops.forEach(shop => {
              this.loadShopHistory(shop.shop._id);
            });
          }
        },
        error: (error) => {
          console.error('Error loading assigned shops:', error);
          this.error.shops = 'Failed to load assigned shops. Please try again.';
        }
      })
    );
  }

  loadDailyReport(): void {
    this.loading.dailyReport = true;
    this.error.dailyReport = '';

    this.subscriptions.add(
      this.marketAreaService.getAggregatedDailyReports(this.selectedDate).pipe(
        finalize(() => {
          this.loading.dailyReport = false;
          this.cdr.markForCheck();
        })
      ).subscribe({
        next: (report) => {
          this.dailyReport = report;
          this.lastUpdateTime = new Date();
        },
        error: (error) => {
          console.error('Error loading daily report:', error);
          this.error.dailyReport = 'Failed to load daily report. Please try again.';
        }
      })
    );
  }

  private loadShopHistory(shopId: string): void {
    this.loading.history = true;
    this.error.history = '';

    this.subscriptions.add(
      this.marketAreaService.getShopAssignmentHistory(shopId).pipe(
        finalize(() => {
          this.loading.history = false;
          this.cdr.markForCheck();
        })
      ).subscribe({
        next: (history) => {
          this.shopHistory = [...this.shopHistory, ...history];
        },
        error: (error) => {
          console.error('Error loading shop history:', error);
          this.error.history = 'Failed to load shop history. Please try again.';
        }
      })
    );
  }

  onDateChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input && input.value) {
      this.selectedDate = input.value;
      this.loadDailyReport();
    }
  }

  refreshDashboard(): void {
    this.dataUpdateSubject.next();
  }

  getTimeUntilNextPoll(): string {
    const now = new Date();
    const nextUpdate = new Date(this.lastUpdateTime.getTime() + this.refreshInterval);
    const diff = nextUpdate.getTime() - now.getTime();
    const seconds = Math.floor(diff / 1000);
    return `${seconds}s`;
  }

  // Helper methods for template
  getTotalShops(): number {
    return this.assignedShops.length;
  }

  getTotalDailyRevenue(): number {
    return this.dailyReport?.totalDailyGP || 0;
  }

  getTotalMonthlyRevenue(): number {
    return this.dailyReport?.totalMonthlyBudget || 0;
  }

  getSuccessRate(): number {
    return this.dailyReport?.metrics?.successRate ?? 0;
  }

  getAverageDailyGP(): number {
    return this.dailyReport?.metrics?.averageDailyGP ?? 0;
  }

  getTotalVariance(): number {
    return this.dailyReport?.metrics?.totalVariance ?? 0;
  }

  getShopPerformance(shopId: string) {
    if (!this.dailyReport?.shopBreakdown || !this.assignedShops) {
      return undefined;
    }
    const assignedShop = this.assignedShops.find(s => s.shop && s.shop._id === shopId);
    if (!assignedShop) {
      return undefined;
    }
    return this.dailyReport.shopBreakdown.find(
      shop => shop.shopName === assignedShop.shop.name
    );
  }

  private checkSetupStatus(): void {
    this.marketAreaService.checkAMSetupStatus().subscribe({
      next: (res) => {
        if (res.success && res.data) {
          this.isSetupComplete = res.data.isSetupComplete;
          this.setupSteps = res.data.setupSteps;
          this.nextSteps = res.data.nextSteps;
          this.showSetupWizard = !res.data.isSetupComplete;
          this.cdr.markForCheck();
        }
      },
      error: (error) => {
        console.error('Error checking setup status:', error);
      }
    });
  }

  // Robust mapping for step status
  getStepStatus(step: string): boolean {
    switch (step) {
      case 'Create market area':
        return !!this.setupSteps.hasMarketArea;
      case 'Assign shops to market area':
        return !!this.setupSteps.hasAssignedShops;
      case 'Valid role assigned':
        return !!this.setupSteps.hasValidRole;
      case 'Valid tenant assigned':
        return !!this.setupSteps.hasValidTenant;
      default:
        return false;
    }
  }

  // GM search logic
  searchGMs(term: string) {
    if (!term || term.length < 2) {
      this.gmSearchResults = [];
      return;
    }
    this.gmSearchLoading = true;
    this.userService.searchGMs(term).subscribe({
      next: (results) => {
        // Filter out already selected GMs
        this.gmSearchResults = results.filter(
          gm => !this.selectedGMs.some(selected => selected.userId === gm.id)
        );
        this.gmSearchLoading = false;
      },
      error: () => {
        this.gmSearchResults = [];
        this.gmSearchLoading = false;
      }
    });
  }

  // Helper to format address object as a string
  formatAddress(address: any): string {
    if (!address) return '';
    if (typeof address === 'string') return address;
    // Adjust these keys to match your address object structure
    const { line1, city, state, zipCode } = address;
    return [line1, city, state, zipCode].filter(Boolean).join(', ');
  }

  onGMSelect(gm: any) {
    this.selectedGM = gm;
    this.marketAreaForm.patchValue({
      marketAreaManager: this.user?._id || '',
      corporateTenant: this.user?.tenant || ''
    });
    // Add to selectedGMs if not already present
    if (!this.selectedGMs.some(selected => selected.userId === gm.id)) {
      this.selectedGMs.push({
        name: gm.name,
        userId: gm.id,
        tenantId: gm.tenant?.id || gm.tenant?._id || gm.tenant,
        tenantName: gm.tenantName || gm.tenant?.name || '',
        tenantAddress: this.formatAddress(gm.tenant?.address)
      });

      this.assignedShopsForm.push({
        shop: gm.tenant,
        generalManager: gm.id
      });
    }

    this.gmSearchResults = [];
    this.marketAreaForm.get('gmSearch')?.setValue('');
  }

  onGMMultiSelect(gms: any[]) {
    // Deduplicate by userId
    const uniqueGMsMap = new Map();
    gms.forEach(gm => {
      if (!uniqueGMsMap.has(gm.id)) {
        uniqueGMsMap.set(gm.id, gm);
      }
    });
    const uniqueGMs = Array.from(uniqueGMsMap.values());
    this.selectedGMs = uniqueGMs.map(gm => ({
      name: gm.name,
      userId: gm.id,
      tenantId: gm.tenant?.id || gm.tenant?._id || gm.tenant,
      tenantName: gm.tenantName || gm.tenant?.name || '',
      tenantAddress: this.formatAddress(gm.tenant?.address)
    }));
  
    // Deduplicate assignedShopsForm by generalManagerId
    const uniqueAssigned = new Map();
    uniqueGMs.forEach(gm => {
      const shopId = gm.tenant?.id || gm.tenant?._id || gm.tenant;
      if (!uniqueAssigned.has(gm.id)) {
        uniqueAssigned.set(gm.id, { shopId, generalManagerId: gm.id });
      }
    });
    this.assignedShopsForm = Array.from(uniqueAssigned.values());

    this.gmSearchResults = [];
    this.marketAreaForm.get('gmSearch')?.setValue('');
  }

  removeGM(userId: string) {
    this.selectedGMs = this.selectedGMs.filter(gm => gm.userId !== userId);
  }

  submitMarketAreaForm() {
    this.marketAreaFormError = '';
    if (this.marketAreaForm.invalid) {
      this.marketAreaFormError = 'Please fill out all required fields.';
      return;
    }
    this.marketAreaFormLoading = true;
    const payload: CreateMarketAreaRequest = {
      name: this.marketAreaForm.get('name')?.value,
      region: this.marketAreaForm.get('region')?.value,
      assignedShops: this.assignedShopsForm
    };

    
    this.marketAreaService.createMarketArea(payload).subscribe({
      next: (res) => {
        this.marketAreaFormLoading = false;
        this.showCreateMarketAreaModal = false;
        this.checkSetupStatus(); // Refresh setup wizard
        this.sysNotifyService.showSuccess('Market area created successfully');

        
      },
      error: (err) => {
        this.marketAreaFormLoading = false;
        this.marketAreaFormError = err.message || 'Failed to create market area';
      }
    });
  }

  dismissSetupWizard() {
    this.showSetupWizard = false;
  }

  openCreateMarketAreaModal() {
    this.showCreateMarketAreaModal = true;
  }

  dismissCreateMarketAreaModal() {
    this.showCreateMarketAreaModal = false;
  }

  toggleSetupWizard(event: Event) {
    event.preventDefault();
    if (
      this.nextSteps.includes('Create market area') &&
      !this.setupSteps.hasMarketArea
    ) {
      this.openCreateMarketAreaModal();
      this.dismissSetupWizard();
    }
    this.cdr.markForCheck();
  }

  // Add helper to get shop info for a GM
  getShopInfoForGM(userId: string) {
    return this.assignedShops.find(s => s.generalManager._id === userId);
  }

  // Add helper to extract city/state from address
  getCityStateFromAddress(address: string): string {
    if (!address) return '';
    const parts = address.split(',');
    if (parts.length >= 3) {
      // Assume format: street, city, state
      return parts[1].trim() + ', ' + parts[2].trim();
    }
    return address;
  }
} 